import classNames from "classnames";
import React from "react";
import { Loader } from "ui-v2/components/Loader";
import { SemanticIconLevels } from "ui-v2/types/colors";
import { TypographyVariantsTypes } from "../../types/typography";
import { Typography } from "../Typography";
import s from "./Button.module.scss";

export type ButtonSize = "large" | "medium" | "small" | "tiny";

export type ButtonVariants =
  | "primary"
  | "secondary_color"
  | "secondary_grey"
  | "tertiary"
  | "positive"
  | "negative"
  | "ghost_color"
  | "ghost_grey"
  | "ghost_black"
  | "link_color"
  | "link_grey"
  | "link_white"
  | "link_positive"
  | "link_negative"
  | "link_black";

const TypographyVariantMap: Record<ButtonSize, TypographyVariantsTypes> = {
  large: "label_1_medium",
  medium: "label_2_medium",
  small: "label_3_medium",
  tiny: "label_4_medium",
};

const LoaderSizes: Record<ButtonSize, number> = {
  tiny: 12,
  small: 16,
  medium: 20,
  large: 24,
};

const LoaderColor: Record<ButtonVariants, SemanticIconLevels> = {
  primary: "on_dark_bg",
  secondary_color: "brand",
  secondary_grey: "primary",
  tertiary: "brand_hover",
  positive: "positive_hover",
  negative: "negative_hover",
  ghost_color: "brand_hover",
  ghost_grey: "tertiary_hover",
  ghost_black: "tertiary_hover",
  link_color: "brand_hover",
  link_grey: "secondary_hover",
  link_white: "on_dark_bg",
  link_positive: "positive_hover",
  link_negative: "negative_hover",
  link_black: "primary_hover",
};

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariants;
  size?: ButtonSize;
  label: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  underline?: boolean;
  fullWidth?: boolean;
}

/**
 * BUTTON
 *
 * @param variant mandatory; ButtonVariants; "primary" | "secondary_color" | "secondary_grey" | "tertiary" etc.
 * @param size mandatory; ButtonSize; "large" | "medium" | "small" | "tiny"
 * @param label mandatory; string; label to be displayed on button,
 * @param leftIcon optional; JSX; left icon, can be any JSX or component
 * @param rightIcon optional; JSX; right icon, can be any JSX or component
 * @param disabled optional; boolean; by default false; whether button is disabled or not
 * @param loading optional; boolean; by default false; to display loading state
 * @param underline optional; boolean; by default false; unvderline text in case of link variants
 * */
export function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size = 'medium',
    label,
    leftIcon,
    rightIcon,
    disabled,
    loading,
    underline,
    fullWidth = false,
    ...propsToFwd
  } = props;

  const buttonClasses = classNames(
    propsToFwd.className,
    s.button,
    s[variant],
    s[size],
    {
      [s.disabled]: disabled,
      [s.loading]: loading,
      [s.fullWidth]: fullWidth,
    }
  );

  return (
    <button {...propsToFwd} className={buttonClasses}>
      {loading && (
        <Loader width={LoaderSizes[size]} color={LoaderColor[variant]} />
      )}
      {!loading && leftIcon && (
        <span className={s.icon_wrapper}>{leftIcon}</span>
      )}
      {!loading && label && (
        <Typography
          label={label}
          variant={TypographyVariantMap[size]}
          className={classNames(s.label, { [s.underline]: underline })}
        />
      )}
      {!loading && rightIcon && (
        <span className={s.icon_wrapper}>{rightIcon}</span>
      )}
    </button>
  );
}
